import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { FileText, Plus, XCircle, DollarSign, Loader2 } from "lucide-react";

const CreditNotesSection = ({ invoiceId, invoiceNumber, invoiceTotal, invoiceStatus, onCreditNoteCreated }) => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCreditNote, setNewCreditNote] = useState({
    amount: "",
    reason: "",
    notes: ""
  });

  const fetchCreditNotes = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const response = await api.get(`/invoices/credit-notes?invoice_id=${invoiceId}`);
      setCreditNotes(response.data);
    } catch (error) {
      console.error("Failed to fetch credit notes:", error);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchCreditNotes();
  }, [fetchCreditNotes]);

  const handleCreateCreditNote = async () => {
    if (!newCreditNote.amount || !newCreditNote.reason) {
      toast.error("Please fill in amount and reason");
      return;
    }

    const amount = parseFloat(newCreditNote.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (amount > invoiceTotal) {
      toast.error("Credit note amount cannot exceed invoice total");
      return;
    }

    setCreating(true);
    try {
      await api.post("/invoices/credit-notes", {
        invoice_id: invoiceId,
        amount: amount,
        reason: newCreditNote.reason,
        notes: newCreditNote.notes || null
      });
      toast.success("Credit note created successfully");
      setShowCreateDialog(false);
      setNewCreditNote({ amount: "", reason: "", notes: "" });
      fetchCreditNotes();
      if (onCreditNoteCreated) onCreditNoteCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create credit note");
    } finally {
      setCreating(false);
    }
  };

  const handleVoidCreditNote = async (creditNoteId) => {
    if (!window.confirm("Are you sure you want to void this credit note?")) return;

    try {
      await api.patch(`/invoices/credit-notes/${creditNoteId}/void`);
      toast.success("Credit note voided");
      fetchCreditNotes();
      if (onCreditNoteCreated) onCreditNoteCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to void credit note");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      issued: "bg-blue-500/20 text-blue-400",
      applied: "bg-green-500/20 text-green-400",
      voided: "bg-red-500/20 text-red-400"
    };
    return <Badge className={styles[status]}>{status.toUpperCase()}</Badge>;
  };

  const totalCreditAmount = creditNotes
    .filter(cn => cn.status !== "voided")
    .reduce((sum, cn) => sum + cn.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Credit Notes
        </h3>
        {invoiceStatus === "paid" && (
          <Button 
            size="sm" 
            onClick={() => setShowCreateDialog(true)}
            data-testid="create-credit-note-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Credit Note
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : creditNotes.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          {invoiceStatus === "paid" 
            ? "No credit notes for this invoice. Create one if you need to adjust the invoice amount."
            : "Credit notes can only be created for paid invoices."}
        </p>
      ) : (
        <div className="space-y-3">
          {creditNotes.map(cn => (
            <Card key={cn.id} className={`${cn.status === 'voided' ? 'opacity-50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-sm font-semibold text-primary">{cn.credit_note_number}</span>
                      {getStatusBadge(cn.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {format(new Date(cn.issued_date), "dd MMM yyyy")}
                    </p>
                    <p className="text-sm font-medium">{cn.reason}</p>
                    {cn.notes && (
                      <p className="text-xs text-muted-foreground mt-1">{cn.notes}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-500">-${cn.amount.toFixed(2)}</p>
                    {cn.status !== "voided" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-400 mt-2"
                        onClick={() => handleVoidCreditNote(cn.id)}
                        data-testid={`void-${cn.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Void
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {totalCreditAmount > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <span className="text-sm font-medium">Total Credits Applied:</span>
              <span className="text-lg font-bold text-green-500">-${totalCreditAmount.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Create Credit Note Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Create Credit Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-secondary/50 text-sm">
              <p className="text-muted-foreground">For Invoice: <span className="text-foreground font-mono">{invoiceNumber}</span></p>
              <p className="text-muted-foreground">Original Amount: <span className="text-foreground font-semibold">${invoiceTotal?.toFixed(2)}</span></p>
            </div>
            
            <div className="space-y-2">
              <Label>Credit Amount *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={invoiceTotal}
                  value={newCreditNote.amount}
                  onChange={(e) => setNewCreditNote(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-7"
                  placeholder="0.00"
                  data-testid="credit-note-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input
                value={newCreditNote.reason}
                onChange={(e) => setNewCreditNote(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Service adjustment, Partial refund"
                data-testid="credit-note-reason"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newCreditNote.notes}
                onChange={(e) => setNewCreditNote(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional details..."
                rows={3}
                data-testid="credit-note-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCreditNote} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Credit Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotesSection;
